/**
 * The **drone** spawn adapter — Core p.105's DRONE tag defaults, no source to freeze from.
 */

import type { CombatantBase } from "@lancer-vtt/domain";
import { ok } from "@lancer-vtt/shared";
import { coreActionIdsFor } from "../coreActionCatalog";
import type { SpawnResult } from "./types";

/**
 * Args for {@link spawnDrone}. Like {@link spawnObject}, a drone has no frame/class
 * to freeze *from* — Core p.105's DRONE tag gives its own flat defaults (SIZE 1/2,
 * 10 EVASION, 5 HP, 0 ARMOR, +0 to all mech skill checks and saves), so the caller
 * names the shape directly rather than a compendium/roster reference.
 */
export type SpawnDroneArgs = {
  combatantId: string;
  name: string;
  /**
   * The **controlling slot** this drone is deployed for — a
   * player slot id for a PC-owned drone, `null` for a GM/NPC-owned one. Frozen once
   * at spawn onto {@link Combatant.controllerSlotId}; {@link "@lancer-vtt/domain" sideOf}
   * reads it back (drone is the one kind whose side isn't purely `kind`-derived).
   */
  controllerSlotId: string | null;
};

/**
 * Spawn a **drone** combatant — Core p.105's DRONE tag flat defaults: SIZE 1/2, 10
 * EVASION, 5 HP, 0 ARMOR, and "make all mech skill checks and saves at +0"
 * (`base.saveBonus: 0`). 0 Speed and no heat/structure/stress track (mirroring {@link spawnObject}) — nothing
 * builds drone-*acting*: a drone never moves or attacks under its own power, so
 * speed/heat/structure/stress are inert by construction, not merely defaulted.
 * `resolveDamageCascade`'s 0-HP branch destroys a `drone` outright like an
 * object/deployable, never the pilot's Down-and-Out path.
 *
 * `kind: "drone"` is the one kind where {@link "@lancer-vtt/domain" isCharacter} and
 * {@link "@lancer-vtt/domain" isActor} diverge: a drone IS a character (tech-
 * targetable, makes saves, hit by AoE) but is NOT an actor (excluded from the turn
 * tracker by the unchanged `isActor` activation-lane filters) — no special-casing
 * needed here, that split lives entirely in the predicates a drone combatant flows
 * through, not in this adapter.
 *
 * **E-DEFENSE — no RAW value is printed for a drone, so this freezes `edef: 8`**,
 * the flat "targets with no E-DEFENSE" fallback stated on the SAME rulebook page as
 * the DRONE tag itself (Core p.105: "Targets with no E-DEFENSE count as having 8
 * E-DEFENSE"). The engine's Smart-weapon branch applies that fallback dynamically
 * for an edef-0 target, but a tech attack reads `base.edef` directly with no
 * fallback — freezing the literal `8` here (rather than `0`) is what keeps a tech attack
 * against a drone a real roll instead of an always-hit DC 0. **A judgment call, not dictated by
 * RAW — flag for review if a different baseline is preferred.**
 *
 * **No `base.sensors`** — RAW never gives a drone its own Sensors stat; "deployed
 * within Sensors and line of sight" (Core p.105-106) is the *deploying character's*
 * Sensors range gating the deploy command, not a stat this combatant itself
 * carries or would ever read as an attacker (nothing builds drone-*acting*).
 *
 * Always succeeds — RAW's own flat defaults, no compendium lookup, nothing to reject.
 */
export function spawnDrone(args: SpawnDroneArgs): SpawnResult {
  const { combatantId, name, controllerSlotId } = args;

  const base: CombatantBase = {
    maxHp: 5,
    // No heat/structure/stress track — a drone isn't built to act, and
    // `resolveDamageCascade`'s no-structure-track branch (`hasStructureTrack`) destroys it
    // outright at 0 HP rather than cascading through a structure table (mirrors
    // object/deployable). `maxHeat`/`maxStructure`/`maxStress` simply omitted: presence-based,
    // no `null` sentinel.
    armor: 0,
    evasion: 10,
    edef: 8, // see the doc comment above — RAW prints no drone E-DEFENSE.
    speed: 0,
    size: 0.5,
    // RAW `:5777`: a DRONE "make[s] all mech skill checks and saves at +0". That is a genuine
    // axis-less FLAT bonus, so it belongs on `saveBonus` (which `saveBonusFor` reads first),
    // not on any HASE axis — a drone carries none. This value did not change when the field
    // was renamed out of `base.save`: it was the one adapter that was always correct.
    saveBonus: 0,
    // A drone at 0 HP is destroyed outright, the same treatment as an object/deployable.
    destructionOutcome: "destroyOutright",
    // The one kind whose side isn't a flat per-kind constant — computed here from the SAME
    // `controllerSlotId` set below, not re-derived later by `sideOf`. A SPAWN-TIME snapshot, not
    // a live link: `controllerSlotId` can be reassigned afterward by the GM-only
    // `set_controller` command, and this stamp does NOT auto-follow that reassignment — see the
    // full rationale on {@link "@lancer-vtt/domain" sideOf}'s doc comment. A GM correcting a
    // reassigned drone's side passes `allegiance` in that same `set_controller` call.
    allegiance: controllerSlotId != null ? "pc" : "npc",
  };

  // A drone's frozen core set is `mech:*`. Nothing builds drone-*acting*, so this set is inert
  // in practice; whether a drone should carry a NARROWER core vocabulary is a content decision,
  // and it would be a one-line change right here, needing no registry edit — which is exactly
  // the payoff of freezing the vocabulary at spawn.
  base.coreActionIds = coreActionIdsFor("mech");

  return ok({
    id: combatantId,
    kind: "drone",
    name,
    base,
    // No `heat` — mirrors `base.maxHeat`'s omission.
    current: { hp: base.maxHp },
    statuses: [],
    pos: null, // unplaced until a `move_token`/`place_token` deploys it onto the map.
    // A spawn-time snapshot of the deployer's own controllerSlotId — the one kind whose
    // `sideOf` isn't purely kind-derived. See the `allegiance` comment above: this is
    // reassignable afterward via the GM-only `set_controller` command, and doing so does NOT
    // auto-follow into the `allegiance` this stamp already froze.
    controllerSlotId,
  });
}
